<template>
  <v-app>
    <v-main>
      <template v-if="isModelRoute && store.accountId !== ''">
        <v-container
          v-if="!store.initialized"
          class="d-flex justify-center align-center"
          style="min-height: 80vh"
        >
          <v-progress-circular
            :size="64"
            :width="6"
            indeterminate
            color="primary"
          />
        </v-container>
        <v-container v-else-if="!store.pageSettings" class="text-center">
          <v-alert type="error" class="mt-10" variant="tonal">
            <h3>Page Not Found</h3>
            <p>The page you are looking for does not exist.</p>
          </v-alert>
        </v-container>
        <EnhancedModelPage v-else />
      </template>
      <router-view v-else-if="$route && !isModelRoute" />
      <div v-else-if="store.accountId !== ''">
        <div v-if="store.initialized && store.pageSettings">
          <EnhancedModelPage />
        </div>
        <v-container v-else-if="store.initialized" class="text-center">
          <v-alert type="error" class="mt-10" variant="tonal">
            <h3>Page Not Found</h3>
            <p>The page you are looking for does not exist.</p>
          </v-alert>
        </v-container>
      </div>
    </v-main>
  </v-app>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useSettingsStore } from './store/settings'
import { useGoogleFonts } from './composables/useGoogleFonts'
import EnhancedModelPage from './views/EnhancedModelPage.vue'

const $route = useRoute()

const isModelRoute = computed(() => {
  return (
    $route.name === 'model-details' ||
    $route.name === 'model-details-short'
  )
})

const store = useSettingsStore()

const { loadFonts, extractUniqueFonts } = useGoogleFonts()
const fontsLoaded = ref(false)
const fontsError = ref<string | null>(null)

onMounted(async () => {
  try {
    if (store.accountId !== '') {
      await store.fetchSettings()
    }

    if (store.initialized && store.pageSettings) {
      console.log('Loading fonts for settings:', store.pageSettings)
      const fontsToLoad = extractUniqueFonts(
        store.settings?.globalSettings as Record<string, unknown> | undefined,
      )

      if (fontsToLoad.length > 0) {
        await loadFonts(fontsToLoad)
        fontsLoaded.value = true
      }
    }
  } catch (error) {
    fontsError.value =
      error instanceof Error ? error.message : 'Failed to load fonts'
    console.error('Font loading error:', error)
  }
})
</script>

<style>
/* Scoped to app roots so WordPress html/body are never targeted. */
.lightning-model-page__root.v-theme--dark,
.lightning-model-page__root .v-theme--dark,
#app.v-theme--dark,
#app .v-theme--dark {
  color: #e0e0e0;
}

.lightning-model-page__root .v-theme--dark .v-application,
#app .v-theme--dark .v-application,
.v-theme--dark.v-application {
  background-color: #121212 !important;
}

.lightning-model-page__root .v-theme--dark .v-main,
#app .v-theme--dark .v-main,
.v-theme--dark .v-main {
  background-color: #121212 !important;
}

.lightning-model-page__root .v-theme--dark .v-overlay,
#app .v-theme--dark .v-overlay,
.v-theme--dark .v-overlay {
  background-color: rgba(0, 0, 0, 0.7) !important;
}

.lightning-model-page__root .v-theme--dark .v-overlay__content,
#app .v-theme--dark .v-overlay__content,
.v-theme--dark .v-overlay__content {
  background-color: #1e1e1e !important;
}

.lightning-model-page__root .v-theme--dark .v-snackbar,
#app .v-theme--dark .v-snackbar,
.v-theme--dark .v-snackbar {
  background-color: #2c2c2c !important;
  color: #e0e0e0 !important;
}

.lightning-model-page__root .v-theme--dark .v-field,
#app .v-theme--dark .v-field,
.v-theme--dark .v-field {
  background-color: transparent !important;
}

.lightning-model-page__root .v-theme--dark .v-field__input,
#app .v-theme--dark .v-field__input,
.v-theme--dark .v-field__input {
  background-color: transparent !important;
  color: #e0e0e0 !important;
}

.lightning-model-page__root .v-theme--dark .v-select .v-field,
.lightning-model-page__root .v-theme--dark .v-text-field .v-field,
#app .v-theme--dark .v-select .v-field,
#app .v-theme--dark .v-text-field .v-field,
.v-theme--dark .v-select .v-field,
.v-theme--dark .v-text-field .v-field {
  background-color: #2c2c2c !important;
}

.lightning-model-page__root .v-theme--dark .v-table,
.lightning-model-page__root .v-theme--dark .v-table__wrapper,
#app .v-theme--dark .v-table,
#app .v-theme--dark .v-table__wrapper,
.v-theme--dark .v-table,
.v-theme--dark .v-table__wrapper {
  background-color: #1e1e1e !important;
}

.lightning-model-page__root .v-theme--dark .v-table__wrapper table tbody tr,
.lightning-model-page__root .v-theme--dark .v-table__wrapper table tbody tr td,
.lightning-model-page__root .v-theme--dark .v-table__wrapper table tbody tr th,
#app .v-theme--dark .v-table__wrapper table tbody tr,
#app .v-theme--dark .v-table__wrapper table tbody tr td,
#app .v-theme--dark .v-table__wrapper table tbody tr th,
.v-theme--dark .v-table__wrapper table tbody tr,
.v-theme--dark .v-table__wrapper table tbody tr td,
.v-theme--dark .v-table__wrapper table tbody tr th {
  background-color: #1e1e1e !important;
  color: #e0e0e0 !important;
}

.lightning-model-page__root .v-theme--dark .v-table__wrapper table thead tr,
.lightning-model-page__root .v-theme--dark .v-table__wrapper table thead tr th,
#app .v-theme--dark .v-table__wrapper table thead tr,
#app .v-theme--dark .v-table__wrapper table thead tr th,
.v-theme--dark .v-table__wrapper table thead tr,
.v-theme--dark .v-table__wrapper table thead tr th {
  background-color: #2c2c2c !important;
  color: #e0e0e0 !important;
}
</style>
